import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import Reveal from '../../components/Reveal/Reveal'
import ArticleCta from '../../components/ArticleCta/ArticleCta'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import useEntryMotion from '../../components/motion/useEntryMotion'
import { resolveTeam } from '../../data/team'
import frameImg from '../../images/process/frame.jpg'
import dataImg from '../../images/process/data.jpg'
import prototypeImg from '../../images/process/prototype.jpg'
import productionImg from '../../images/process/production.jpg'
import supportImg from '../../images/process/support.jpg'
import './process.scss'

const STAGE_COUNT = 5

// Stock scenes (Unsplash) showing the kind of work at each stage, in stage
// order. Not our team: see src/images/process/README.md
const STAGE_IMAGES = [frameImg, dataImg, prototypeImg, productionImg, supportImg]

/**
 * How a project runs, stage by stage.
 *
 * The angle that sets this apart from the usual "discover → build → launch"
 * page: every stage ends with a gate the client decides at, and names what
 * they keep if they stop there. That is the promise buyers are nervous about
 * (sunk cost, lock-in), so the page is organised around it rather than
 * around our activities.
 *
 * The stage rail on the left follows the reader down the page. Every stage
 * is always in the DOM, so search engines and the prerender see the whole
 * process regardless of scroll position.
 */
const Process = () => {
  const { translations: t } = useContext(LanguageContext)
  const navigate = useNavigate()
  const animateIn = useEntryMotion()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const stages = useMemo(
    () =>
      Array.from({ length: STAGE_COUNT }, (_, i) => {
        const n = i + 1
        return {
          id: `stage-${n}`,
          number: String(n).padStart(2, '0'),
          name: t[`procS${n}Name`],
          time: t[`procS${n}Time`],
          text: t[`procS${n}Text`],
          gets: [t[`procS${n}Get1`], t[`procS${n}Get2`], t[`procS${n}Get3`]],
          gate: t[`procS${n}Gate`],
          keep: t[`procS${n}Keep`],
          optional: n === STAGE_COUNT,
          image: STAGE_IMAGES[i],
        }
      }),
    [t]
  )

  const principles = [
    { title: t.procPrin1Title, text: t.procPrin1Text },
    { title: t.procPrin2Title, text: t.procPrin2Text },
    { title: t.procPrin3Title, text: t.procPrin3Text },
  ]

  // Only the people a client actually works with during delivery
  const team = resolveTeam(t, { deliveryOnly: true })

  const rhythm = [
    { title: t.procRhythm1Title, text: t.procRhythm1Text },
    { title: t.procRhythm2Title, text: t.procRhythm2Text },
    { title: t.procRhythm3Title, text: t.procRhythm3Text },
  ]

  // Scroll-spy for the stage rail: whichever stage crosses the upper part of
  // the viewport is the active one.
  const [active, setActive] = useState(0)
  const sectionRefs = useRef([])

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(Number(entry.target.dataset.index))
          }
        })
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
    )
    sectionRefs.current.forEach((el) => el && observer.observe(el))
    return () => observer.disconnect()
  }, [stages])

  const jumpTo = (index) => {
    const el = sectionRefs.current[index]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const heroMotion = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 18 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  })

  return (
    <Layout>
      <Seo
        title={t.procSeoTitle}
        description={t.procSeoDescription}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Our process', path: '/process' },
          ]),
        ]}
      />

      <div className='proc-page'>
        {/* Hero */}
        <section className='proc-hero'>
          <div className='proc-inner'>
            <motion.p className='proc-kicker' {...heroMotion(0)}>
              {t.procKicker}
            </motion.p>
            <motion.h1 className='proc-title' {...heroMotion(0.08)}>
              {t.procTitle}
            </motion.h1>
            <motion.p className='proc-sub' {...heroMotion(0.16)}>
              {t.procSub}
            </motion.p>
            <motion.div className='proc-hero-actions' {...heroMotion(0.24)}>
              <button className='proc-btn' onClick={() => navigate('/contact-us')}>
                {t.procCtaPrimary} <span className='proc-btn-arrow'>→</span>
              </button>
              <button className='proc-btn proc-btn-ghost' onClick={() => navigate('/services')}>
                {t.procCtaSecondary}
              </button>
            </motion.div>
            <motion.div className='proc-hero-team' {...heroMotion(0.32)}>
              <ul className='proc-faces' aria-hidden='true'>
                {team.map((member) => (
                  <li key={member.id}><img src={member.src} alt='' /></li>
                ))}
              </ul>
              <p className='proc-hero-team-text'>{t.procHeroTeam}</p>
            </motion.div>
          </div>
        </section>

        {/* The path: every stage at a glance, with its place on the calendar */}
        <section className='proc-path-wrap' aria-label={t.procPathLabel}>
          <div className='proc-inner'>
            <Reveal>
              <p className='proc-path-label'>{t.procPathLabel}</p>
              <ol className='proc-path'>
                {stages.map((stage, index) => (
                  <li
                    key={stage.id}
                    className={`proc-path-node ${stage.optional ? 'proc-path-node-optional' : ''}`}
                  >
                    <button type='button' className='proc-path-btn' onClick={() => jumpTo(index)}>
                      <span className='proc-path-dot' aria-hidden='true' />
                      <span className='proc-path-num'>{stage.number}</span>
                      <span className='proc-path-name'>{stage.name}</span>
                      <span className='proc-path-time'>{stage.time}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </section>

        {/* Principles */}
        <section className='proc-principles-wrap'>
          <div className='proc-inner'>
            <div className='proc-principles'>
              {principles.map((item, index) => (
                <Reveal key={item.title} delay={index * 0.08} className='proc-principle'>
                  <span className='proc-principle-num'>{String(index + 1).padStart(2, '0')}</span>
                  <h2 className='proc-principle-title'>{item.title}</h2>
                  <p className='proc-principle-text'>{item.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Stages */}
        <section className='proc-stages-wrap'>
          <div className='proc-inner proc-stages-layout'>
            <aside className='proc-rail' aria-label={t.procRailLabel}>
              <p className='proc-rail-label'>{t.procRailLabel}</p>
              <ol className='proc-rail-list'>
                {stages.map((stage, index) => (
                  <li key={stage.id}>
                    <button
                      type='button'
                      className={`proc-rail-item ${active === index ? 'proc-rail-item-active' : ''} ${
                        active > index ? 'proc-rail-item-done' : ''
                      }`}
                      onClick={() => jumpTo(index)}
                      aria-current={active === index ? 'true' : undefined}
                    >
                      <span className='proc-rail-num'>{stage.number}</span>
                      <span className='proc-rail-name'>{stage.name}</span>
                    </button>
                  </li>
                ))}
              </ol>
              <div className='proc-rail-track' aria-hidden='true'>
                <span
                  className='proc-rail-progress'
                  style={{ height: `${((active + 1) / STAGE_COUNT) * 100}%` }}
                />
              </div>
            </aside>

            <div className='proc-stages'>
              {stages.map((stage, index) => (
                <article
                  key={stage.id}
                  id={stage.id}
                  data-index={index}
                  ref={(el) => {
                    sectionRefs.current[index] = el
                  }}
                  className={`proc-stage ${stage.optional ? 'proc-stage-optional' : ''}`}
                >
                  <Reveal>
                    <header className='proc-stage-head'>
                      <span className='proc-stage-num'>{stage.number}</span>
                      <div className='proc-stage-head-text'>
                        <h2 className='proc-stage-name'>{stage.name}</h2>
                        <span className='proc-stage-time'>{stage.time}</span>
                      </div>
                    </header>

                    <div className='proc-stage-photo'>
                      <img src={stage.image} alt='' loading='lazy' />
                    </div>

                    <p className='proc-stage-label'>{t.procWhatLabel}</p>
                    <p className='proc-stage-text'>{stage.text}</p>

                    <div className='proc-stage-grid'>
                      <div className='proc-stage-get'>
                        <p className='proc-stage-label'>{t.procGetLabel}</p>
                        <ul>
                          {stage.gets.map((item) => (
                            <li key={item}>
                              <span className='proc-check' aria-hidden='true' />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className='proc-gate'>
                        <p className='proc-gate-label'>{t.procGateLabel}</p>
                        <p className='proc-gate-question'>{stage.gate}</p>
                        <div className='proc-gate-keep'>
                          <p className='proc-gate-keep-label'>{t.procKeepLabel}</p>
                          <p className='proc-gate-keep-text'>{stage.keep}</p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Rhythm */}
        <section className='proc-rhythm-wrap'>
          <div className='proc-inner'>
            <Reveal>
              <p className='proc-kicker'>{t.procRhythmKicker}</p>
              <h2 className='proc-section-title'>{t.procRhythmTitle}</h2>
            </Reveal>
            <div className='proc-rhythm'>
              {rhythm.map((item, index) => (
                <Reveal key={item.title} delay={index * 0.08} className='proc-rhythm-item'>
                  <span className='proc-rhythm-one' aria-hidden='true'>1</span>
                  <h3 className='proc-rhythm-title'>{item.title}</h3>
                  <p className='proc-rhythm-text'>{item.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* The people behind the process */}
        <section className='proc-team-wrap'>
          <div className='proc-inner'>
            <Reveal>
              <p className='proc-kicker'>{t.procTeamKicker}</p>
              <h2 className='proc-section-title'>{t.procTeamTitle}</h2>
              <p className='proc-team-intro'>{t.procTeamText}</p>
            </Reveal>
            <div className='proc-team'>
              {team.map((member, index) => (
                <Reveal key={member.id} delay={(index % 3) * 0.08} className='proc-member'>
                  <div className='proc-member-photo'>
                    <img src={member.src} alt={member.name} loading='lazy' />
                  </div>
                  <p className='proc-member-name'>{member.name}</p>
                  <p className='proc-member-role'>{member.role}</p>
                </Reveal>
              ))}
            </div>
            <button type='button' className='proc-team-link' onClick={() => navigate('/about-us')}>
              {t.procTeamLink} <span aria-hidden='true'>→</span>
            </button>
          </div>
        </section>

        <section className='proc-cta-wrap'>
          <div className='proc-inner'>
            <ArticleCta />
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default Process
